import React, { useState, useContext, useEffect } from 'react';
import { Toaster } from 'sonner';
import { EmployeeCard } from './EmployeeCard';
import { EmployeeSearch } from './EmployeeSearch';
import { AddEmployeeDialog } from './AddEmployeeDialog';
import { AdminDaysManager } from './AdminDaysManager';
import { VacationRequestDialog } from './VacationRequestDialog';
import { EmployeeDetailView } from './EmployeeDetailView';
import { useEmployees } from '../../hooks/useEmployees';
import { UserContext } from '../../context/UserContext';
import { Plus, Settings } from 'lucide-react';
import './vacaciones.css';

export const VacacionesDashboard = () => {
  const { currentUser } = useContext(UserContext);
  const { employees, loading, error, loadEmployees, updateEmployee } = useEmployees();
  const [filtered, setFiltered] = useState(employees);

  useEffect(() => {
    setFiltered(employees);
  }, [employees]);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAdminManager, setShowAdminManager] = useState(false);
  const [showVacationRequest, setShowVacationRequest] = useState(false);
  const [showEmployeeDetail, setShowEmployeeDetail] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const filteredEmployees = filtered && filtered.length ? filtered : employees;

  const handleRequestVacation = (employee) => {
    if (employee && employee.id) {
      setSelectedEmployee(employee);
      setShowVacationRequest(true);
    }
  };

  const handleAddDays = (employeeId, type) => {
    // Buscar al empleado y abrir la vista de detalles
    const employee = employees.find(emp => (emp.id === employeeId || emp._id === employeeId));
    if (employee) {
      setSelectedEmployee(employee);
      setShowEmployeeDetail(true);
    }
  };

  const handleViewDetails = (employeeId) => {
    // Buscar al empleado y mostrar la vista completa de detalles
    const employee = employees.find(emp => (emp.id === employeeId || emp._id === employeeId));
    if (employee) {
      setSelectedEmployee(employee);
      setShowEmployeeDetail(true);
    }
  };

  const handleBackFromDetail = async () => {
    setShowEmployeeDetail(false);
    setSelectedEmployee(null);
    // Al volver al dashboard, recargar empleados para reflejar cambios hechos en el detalle
    await loadEmployees();
  };

  const handleAdminDataUpdated = async () => {
    // Recargar datos del dashboard cuando se actualicen desde el panel de administración
    await loadEmployees();
  };

  const handleUpdateEmployee = async (employeeId, updates) => {
    try {
      await updateEmployee(employeeId, updates);
      await loadEmployees(); // Recargar la lista después de actualizar
      return true;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw error;
    }
  };

  const handleCloseVacationRequest = (open) => {
    setShowVacationRequest(open);
    if (!open) {
      setSelectedEmployee(null);
    }
  };

  // Si está mostrando el detalle del empleado, renderizar la vista completa
  if (showEmployeeDetail && selectedEmployee) {
    return (
      <EmployeeDetailView 
        employeeId={selectedEmployee.id || selectedEmployee._id}
  onBack={handleBackFromDetail}
  onRefreshEmployees={loadEmployees}
      />
    );
  }

  // Verificar si el usuario es ADMIN o FACTURACION
  if (!currentUser || (currentUser.role !== 'ADMIN' && currentUser.role !== 'FACTURACION')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-4 text-red-300">
            <svg fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM13 14H11V7H13V14ZM13 18H11V16H13V18Z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acceso Denegado</h3>
          <p className="text-gray-600">Solo los administradores y personal de facturación pueden acceder al sistema de vacaciones.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-4 text-red-300">
            <svg fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error de Conexión</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando empleados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Sistema de Gestión de Vacaciones
            </h1>
            <p className="text-gray-600">
              {employees.length} empleado{employees.length !== 1 ? 's' : ''} registrado{employees.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAdminManager(true)}
              className="btn btn-secondary"
            >
              <Settings className="w-4 h-4 mr-2" />
              Administrar
            </button>
            <button
              onClick={() => setShowAddEmployee(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Empleado
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <EmployeeSearch
          employees={employees}
          onFilteredEmployees={setFiltered}
        />
      </div>

      {/* Employee Grid */}
      {filteredEmployees.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 mx-auto mb-6 text-gray-300">
            <svg fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 21H5V3H13V9H19Z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            {employees.length > 0 ? 'No se encontraron empleados con los filtros' : 'No hay empleados registrados'}
          </h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {employees.length > 0
              ? 'Refina tu búsqueda o cambia el orden.'
              : 'Comienza agregando tu primer empleado al sistema'
            }
          </p>
          {employees.length === 0 && (
            <button
              onClick={() => setShowAddEmployee(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Primer Empleado
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredEmployees.map((employee) => (
            <EmployeeCard
              key={employee.id || employee._id}
              employee={employee}
              onRequestVacation={() => handleRequestVacation(employee)}
              onAddDays={handleAddDays}
              onViewDetails={handleViewDetails}
              onUpdateEmployee={handleUpdateEmployee}
              onRefreshEmployees={loadEmployees}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <AddEmployeeDialog
        open={showAddEmployee}
        onOpenChange={setShowAddEmployee}
        onEmployeeAdded={handleAdminDataUpdated}
      />

      <AdminDaysManager
        open={showAdminManager}
        onOpenChange={setShowAdminManager}
        onDataUpdated={handleAdminDataUpdated}
      />

      {selectedEmployee && (
        <VacationRequestDialog
          employee={selectedEmployee}
          open={showVacationRequest}
          onOpenChange={handleCloseVacationRequest}
        />
      )}
    </div>
  );
};
